import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope'
import { faIdCard } from '@fortawesome/pro-regular-svg-icons/faIdCard'
import {
  Box,
  Flex,
  Grid,
  Select,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'

import { useQueryGetBusinessCategories } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import {
  CollapsibleCard,
  Icon,
  SelectCountry,
  InputPhoneNumber,
  RichTextEditor,
} from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import { useBusinessInfoFormContext } from './BusinessInfoFormProvider'

export function GeneralInfoSection() {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_business' })
  const { isMobile } = useMediaQuery()
  const form = useBusinessInfoFormContext()

  const { data, isLoading } = useQueryGetBusinessCategories()
  const businessCategories = {
    data: (data ?? []).map((category) => ({
      value: category.id,
      label: category.name,
    })),
    isLoading,
  }

  return (
    <Box mt='md' mb='lg'>
      <CollapsibleCard opened>
        <CollapsibleCard.Title>
          {t('section_general_info.title')}
        </CollapsibleCard.Title>
        <CollapsibleCard.Description>
          {t('section_general_info.description')}
        </CollapsibleCard.Description>
        <CollapsibleCard.Icon withCircleBackground icon={faIdCard} />
        <CollapsibleCard.Content>
          <Box>
            <TextInput
              mb='lg'
              label={t('section_general_info.fields.company_name_label')}
              withAsterisk
              {...form.getInputProps('business_name')}
            />
            <SelectCountry
              mb='lg'
              label={t(
                'section_general_info.fields.country_of_operations_label'
              )}
              description={t(
                'section_general_info.fields.country_of_operations_description'
              )}
              withAsterisk
              disabled
              {...form.getInputProps('legal_address.address_country_code')}
            />
            <Select
              mb='lg'
              label={t('section_general_info.fields.business_sector_label')}
              placeholder={t(
                'section_general_info.fields.business_sector_placeholder'
              )}
              data={businessCategories.data}
              disabled={businessCategories.isLoading}
              {...form.getInputProps('business_category_id')}
            />
            <Grid mt='sm'>
              <Grid.Col span={isMobile ? 12 : 6}>
                <TextInput
                  w='100%'
                  label={t('section_general_info.fields.public_email_label')}
                  leftSection={<Icon icon={faEnvelope} />}
                  withAsterisk
                  {...form.getInputProps('public_email')}
                />
              </Grid.Col>
              <Grid.Col mb='md' span={isMobile ? 12 : 6}>
                <InputPhoneNumber
                  w='100%'
                  label={t(
                    'section_general_info.fields.public_phone_number_label'
                  )}
                  withAsterisk
                  onChangeDialCode={(code: string) =>
                    form.setFieldValue('public_phone_number_country_code', code)
                  }
                  valueDialCode={form.values.public_phone_number_country_code}
                  {...form.getInputProps('public_phone_number')}
                />
              </Grid.Col>
            </Grid>
            <Box mt='md'>
              <Flex mb='sm' align='center' gap='sm'>
                <Text fw={500}>
                  {t('section_general_info.fields.terms_and_conditions_label')}
                </Text>
                <Tooltip
                  w={230}
                  label={t(
                    'section_general_info.fields.terms_and_conditions_description'
                  )}
                  position='right'
                  multiline
                >
                  <Box pt={2}>
                    <Icon icon={faInfoCircle} size='sm' color='dimmed' />
                  </Box>
                </Tooltip>
              </Flex>
              <RichTextEditor {...form.getInputProps('terms_and_conditions')} />
            </Box>
          </Box>
        </CollapsibleCard.Content>
      </CollapsibleCard>
    </Box>
  )
}
